import {
  CatalogueList,
  PlatformBadge,
  newestPublished,
  newestVersion,
  type CatalogueVersionRow,
} from '../catalogue/CatalogueList';
import { ghostButtonClass, primaryButtonClass } from './controls';
import type { ReactNode } from 'react';

/**
 * §9.0 — the catalogue.
 *
 * <b>A system is authored without reference to any leaderboard.</b> That is what this screen is
 * for and why it is its own screen: systems are reusable, and conflating them with the
 * leaderboard that consumes one is the main design risk the spec names.
 *
 * An operator sees their room's systems and the platform's in one list, told apart by the shield
 * and by what the row offers: a platform row has **no edit control at all** rather than a
 * disabled one, and offers *Clone to edit* instead. A control that looks available and refuses is
 * worse than an absent one.
 *
 * The three delete outcomes of §2.4 must not be blurred, so nothing here decides them: the row
 * asks, the server answers, and a refusal comes back leading with Archive.
 *
 * <b>The table itself is `CatalogueList`, shared with the fantasy salary model catalogue.</b>
 * `fantasy-contests.md` §4.1 asks for that screen to be this one with a different noun; two
 * implementations of it would agree only until one was changed. What stays here is what is
 * specific to points systems: the copy, and §9.7's empty state, which offers a real published
 * ladder as a first draft.
 */

export type PointsSystemVersionRow = CatalogueVersionRow;

export interface PointsSystemRow {
  id: string;
  name: string;
  description: string | null;
  isPlatform: boolean;
  isArchived: boolean;
  clonedFromSystemId: string | null;
  versions: PointsSystemVersionRow[];
}

export function PointsSystemList({
  systems,
  usage,
  usageLabel,
  usageHint,
  includeArchived,
  onIncludeArchivedChange,
  onNew,
  onOpen,
  onClone,
  onArchive,
  onUnarchive,
  onDelete,
  canManage,
  canCreate = true,
  nameOf,
  notice,
}: {
  systems: readonly PointsSystemRow[];
  /**
   * How many leaderboards use each system, or null where the client hasn't been given the
   * leaderboards to count. Null renders as "—", never as 0: "no leaderboard uses this" and "this
   * screen doesn't know" send an operator to very different places.
   */
  usage: ReadonlyMap<string, number> | null;
  usageLabel: string;
  usageHint?: string;
  includeArchived: boolean;
  onIncludeArchivedChange: (next: boolean) => void;
  onNew: (start: 'blank' | 'shrpo') => void;
  onOpen: (system: PointsSystemRow) => void;
  onClone?: (system: PointsSystemRow) => void;
  onArchive: (system: PointsSystemRow) => void;
  onUnarchive: (system: PointsSystemRow) => void;
  onDelete: (system: PointsSystemRow) => void;
  /** False for a platform system seen by a room: visible, usable, never editable. */
  canManage: (system: PointsSystemRow) => boolean;
  canCreate?: boolean;
  /** Resolves a cloned-from id to a name, for the provenance line. */
  nameOf?: (id: string) => string | null;
  notice?: ReactNode;
}) {
  return (
    <CatalogueList
      items={systems}
      facts={(system) => ({
        id: system.id,
        name: system.name,
        description: system.description,
        isPlatform: system.isPlatform,
        isArchived: system.isArchived,
        clonedFromId: system.clonedFromSystemId,
        versions: system.versions,
      })}
      copy={{
        title: 'Points systems',
        rowLabel: 'System',
        blurb:
          'How finishes turn into points. A system is built and tested here on its own; a leaderboard picks one up when it starts, and keeps that exact version for good.',
        newLabel: 'New system',
        usageLabel,
        usageHint,
      }}
      usage={usage}
      includeArchived={includeArchived}
      onIncludeArchivedChange={onIncludeArchivedChange}
      onNew={() => onNew('blank')}
      onOpen={onOpen}
      onClone={onClone}
      onArchive={onArchive}
      onUnarchive={onUnarchive}
      onDelete={onDelete}
      canManage={canManage}
      canCreate={canCreate}
      nameOf={nameOf}
      notice={notice}
      emptyState={
        <EmptyState onNew={onNew} canCreate={canCreate} includeArchived={includeArchived} />
      }
    />
  );
}

/**
 * §9.7 — start from something rather than a blank form.
 *
 * Somebody meeting this screen for the first time has no idea what a placement table looks like.
 * The reference ladder is a real, published system whose numbers this codebase has checked
 * against real standings, so it is a better first draft than an empty grid — and every figure in
 * it is editable the moment it lands.
 */
function EmptyState({
  onNew,
  canCreate,
  includeArchived,
}: {
  onNew: (start: 'blank' | 'shrpo') => void;
  canCreate: boolean;
  includeArchived: boolean;
}) {
  if (!canCreate) {
    return (
      <p className="px-5 py-10 text-center text-sm text-ink-800/55">
        No points systems are available to this room yet.
      </p>
    );
  }

  return (
    <div className="px-5 py-10 text-center">
      <p className="text-sm font-medium text-ink-950">No points systems yet</p>
      <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-ink-800/60">
        A points system decides what a finish is worth — 100 for a win, less as you go down, more
        in a big field. Start from a published ladder and change what you don’t like.
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        <button type="button" onClick={() => onNew('shrpo')} className={primaryButtonClass}>
          Start from the SHRPO ladder
        </button>
        <button type="button" onClick={() => onNew('blank')} className={ghostButtonClass}>
          Start from scratch
        </button>
      </div>
      {!includeArchived ? (
        <p className="mt-3 text-[11px] text-ink-800/40">
          Archived systems are hidden — tick “Show archived” if you are looking for one.
        </p>
      ) : null}
    </div>
  );
}

export { PlatformBadge };

/** The version a reader means by "the current one": the highest number, whatever its status. */
export function currentVersion(system: PointsSystemRow): PointsSystemVersionRow | null {
  return newestVersion(system.versions);
}

/** The newest published version — what a leaderboard starting today would pin. */
export function latestPublished(system: PointsSystemRow): PointsSystemVersionRow | null {
  return newestPublished(system.versions);
}
